
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { availableAgents } from "@/utils/a2a";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Info, BrainCircuit } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

interface AgentConfigurationProps {
  a2aEnabled: boolean;
  multiAgentEnabled: boolean;
  onA2AChange: (enabled: boolean) => void;
  onMultiAgentChange: (enabled: boolean) => void;
}

export const AgentConfiguration = ({
  a2aEnabled,
  multiAgentEnabled,
  onA2AChange,
  onMultiAgentChange
}: AgentConfigurationProps) => {
  return (
    <div className="space-y-4">
      <Alert className="bg-primary/10 border-primary/20">
        <Info className="h-4 w-4 text-primary" />
        <AlertTitle>BrigAI Orchestration System</AlertTitle>
        <AlertDescription>
          BrigAI functions as your central AI orchestrator, automatically coordinating specialized agents based on your requests.
          Configure the orchestration behavior below.
        </AlertDescription>
      </Alert>
      
      <Card className="border border-border/50">
        <CardContent className="p-6">
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base font-medium">A2A Protocol</Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Enable Agent-to-Agent communication protocol
                </p>
              </div>
              <Switch
                checked={a2aEnabled}
                onCheckedChange={onA2AChange}
                defaultChecked={true}
              />
            </div>
            
            {a2aEnabled && (
              <div className="space-y-4 pt-4 border-t">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base font-medium">Multi-Agent Collaboration</Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Allow agents to work together on complex tasks
                    </p>
                  </div>
                  <Switch
                    checked={multiAgentEnabled}
                    onCheckedChange={onMultiAgentChange}
                    defaultChecked={true}
                  />
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      
      {a2aEnabled && (
        <div className="space-y-4 mt-6">
          <div className="flex items-center">
            <BrainCircuit className="h-5 w-5 text-primary mr-2" />
            <h3 className="text-lg font-medium">Available Specialized Agents</h3>
          </div>
          <p className="text-sm text-muted-foreground">
            These specialized agents are automatically activated by BrigAI when their capabilities are needed.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            {availableAgents.filter(agent => agent.id !== 'executive-agent').map(agent => (
              <Card key={agent.id} className="border border-border/50">
                <CardContent className="p-4">
                  <div className="flex items-start space-x-3">
                    <Switch id={agent.id} defaultChecked={true} />
                    <div className="space-y-1">
                      <div className="flex items-center space-x-2">
                        <Label htmlFor={agent.id} className="font-medium">{agent.name}</Label>
                        <Badge variant="outline" className="text-xs">A2A</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">{agent.description}</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {agent.capabilities.slice(0, 3).map(capability => (
                          <Badge key={capability} variant="secondary" className="text-xs">
                            {capability}
                          </Badge>
                        ))}
                        {agent.capabilities.length > 3 && (
                          <Badge variant="outline" className="text-xs">
                            +{agent.capabilities.length - 3} more
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
